import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';

export type ConfirmSeverity = 'warning' | 'error' | 'info';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  severity?: ConfirmSeverity;
  /** Disables both buttons while the confirmed action is in flight. */
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Themed replacement for `window.confirm()` on destructive / irreversible
 * actions. Controlled component — pair with `useDialog()` when you need
 * bespoke state, or use the `useConfirm()` hook below for the common
 * "ask, then act" flow with zero state plumbing.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  severity = 'warning',
  isLoading = false,
  onConfirm,
  onCancel,
}) => (
  <Dialog open={open} onClose={isLoading ? undefined : onCancel} maxWidth="sm" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <Alert severity={severity} sx={{ mt: 0.5 }}>
        {message}
      </Alert>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={isLoading}>
        {cancelLabel}
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        color={severity === 'error' ? 'error' : severity === 'warning' ? 'warning' : 'primary'}
        disabled={isLoading}
        autoFocus
      >
        {confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

export interface ConfirmOptions {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  severity?: ConfirmSeverity;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * Promise-based confirm — the drop-in `window.confirm` replacement:
 *
 *   const confirm = useConfirm();
 *   ...
 *   if (await confirm({ title: 'Delete task', message: 'This cannot be undone.', severity: 'error', confirmLabel: 'Delete' })) {
 *     deleteMutation.mutate(id);
 *   }
 *
 * Resolves `true` on confirm, `false` on cancel/dismiss. Requires
 * `<ConfirmProvider>` near the app root.
 */
export const useConfirm = (): ConfirmFn => {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error('useConfirm() requires a <ConfirmProvider> ancestor');
  }
  return confirm;
};

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      // If a confirm is somehow already open, dismiss it as cancelled.
      resolverRef.current?.(false);
      resolverRef.current = resolve;
      setOptions(opts);
    });
  }, []);

  const settle = useCallback((confirmed: boolean) => {
    resolverRef.current?.(confirmed);
    resolverRef.current = null;
    setOptions(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={options !== null}
        title={options?.title ?? ''}
        message={options?.message ?? ''}
        confirmLabel={options?.confirmLabel}
        cancelLabel={options?.cancelLabel}
        severity={options?.severity}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </ConfirmContext.Provider>
  );
};

export default ConfirmDialog;
